/**
 * Local (email + password) session store.
 *
 * The active session is EITHER an MSAL (Entra) session OR a local session.
 * A local session is represented by a plain HS256 JWT issued by the backend
 * (POST /auth/local/login) and stored in localStorage under
 * 'periscope-local-token'. useAuth() consults this store first: when a local
 * token exists, all API calls attach it as the Bearer token and MSAL is never
 * invoked.
 *
 * Implemented as a tiny external store (subscribe/getSnapshot) so React
 * components can bind to it via useSyncExternalStore.
 */

const STORAGE_KEY = 'periscope-local-token';

type Listener = () => void;
const listeners = new Set<Listener>();

let cachedToken: string | null = readFromStorage();

function readFromStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getLocalToken(): string | null {
  return cachedToken;
}

export function setLocalToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Storage unavailable (private mode quota etc.) — keep in-memory session.
  }
  cachedToken = token;
  notify();
}

export function clearLocalToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  cachedToken = null;
  notify();
}

export function subscribeLocalSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Decoded (unverified) JWT payload of the local token. */
export interface LocalTokenPayload {
  sub?: string;
  email?: string;
  provider?: string;
  exp?: number;
}

export function decodeLocalToken(token: string): LocalTokenPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as LocalTokenPayload;
  } catch {
    return null;
  }
}

/** True when the token's exp claim has passed (30 s clock skew margin). */
export function isLocalTokenExpired(token: string): boolean {
  const payload = decodeLocalToken(token);
  if (!payload?.exp) return false; // no exp claim — let the backend decide
  return payload.exp * 1000 <= Date.now() + 30_000;
}

// Cross-tab sync: another tab logging in/out updates this tab's session.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      cachedToken = readFromStorage();
      notify();
    }
  });
}
